import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ArrowLeft, UserPlus, Paperclip, Send, Users, Coins, Sparkles } from 'lucide-react';
import { toast } from 'sonner';
import { aiAgentApi } from '../../../lib/aiAgentApi';
import { getCurrentAccount } from '../../../lib/auth';
import { MessageList } from '../MessageList';
import { InvitePicker } from './InvitePicker';

/**
 * Full-screen chat detail view (within the drawer). Header shows the chat
 * title and an invite-teammate button (+). Bottom is composer with paperclip
 * attach and send arrow per wireframe.
 */
export const ChatDetail = ({ conv, onBack, onChanged, resumePrompt, onResumeConsumed, blocked = false, personaName, onTopUp, refreshWallet }) => {
  const agentName = personaName || (() => {
    try { return localStorage.getItem('ai_agent_persona_name') || 'Astrid'; } catch { return 'Astrid'; }
  })();
  const [messages, setMessages] = useState([]);
  const [busy, setBusy] = useState(false);
  const [showInvite, setShowInvite] = useState(false);
  const [collabCount, setCollabCount] = useState(conv?.collaborator_user_ids?.length || 0);
  const [draft, setDraft] = useState('');
  const [attachments, setAttachments] = useState([]); // [{file_id, s3_key, filename, content_type, size_bytes}]
  const [liveTool, setLiveTool] = useState(null); // tool currently running (live progress)

  // Auto-grow textarea: min 100px, max 300px (then scroll).
  const taRef = useRef(null);
  useEffect(() => {
    const ta = taRef.current;
    if (!ta) return;
    ta.style.height = 'auto';
    const next = Math.min(Math.max(ta.scrollHeight, 100), 300);
    ta.style.height = next + 'px';
  }, [draft]);
  const [hasMore, setHasMore] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [totalMessages, setTotalMessages] = useState(0);

  const currentAccount = useMemo(() => getCurrentAccount(), []);
  const accountId = currentAccount?.id || null;

  // Load the LATEST page of messages on open (page size = 5).
  useEffect(() => {
    if (!conv?.id) return;
    let cancelled = false;
    (async () => {
      try {
        const detail = await aiAgentApi.getConversation(conv.id, { limit: 5, include_tool: false });
        if (cancelled) return;
        setMessages(detail.messages || []);
        setCollabCount(detail.conversation?.collaborator_user_ids?.length || 0);
        setHasMore(Boolean(detail.has_more));
        setTotalMessages(detail.total_messages || (detail.messages || []).length);
      } catch {
        if (!cancelled) toast.error('Could not open this chat');
      }
    })();
    return () => { cancelled = true; };
  }, [conv?.id]);

  // Merge server messages into state, de-duped by id (used by turn-resume and
  // scroll-back so a message is never rendered twice).
  const mergeMessages = useCallback((incoming) => {
    if (!incoming || incoming.length === 0) return;
    setMessages((prev) => {
      const seen = new Set(prev.map((m) => m.id).filter(Boolean));
      const additions = incoming.filter((m) => m.id && !seen.has(m.id));
      return additions.length ? [...prev, ...additions] : prev;
    });
  }, []);

  // Resume an in-flight turn on open. If a turn is still running server-side
  // (user refreshed / navigated away / closed the laptop mid-turn), re-show the
  // processing indicator and, when it finishes, pull the freshly-saved reply so
  // the OUTCOME is shown — never a silent revert. Read-only: this NEVER
  // re-sends the turn, so there is no extra server load or duplicate work.
  useEffect(() => {
    if (!conv?.id) return;
    let stopped = false;
    let interval = null;
    (async () => {
      try {
        const p = await aiAgentApi.getTurnProgress(conv.id);
        if (stopped || !p?.active) return;
        setBusy(true);
        setLiveTool(p.tool || null);
        interval = setInterval(async () => {
          try {
            const pr = await aiAgentApi.getTurnProgress(conv.id);
            if (pr?.active) { setLiveTool(pr.tool || null); return; }
            if (interval) { clearInterval(interval); interval = null; }
            const detail = await aiAgentApi.getConversation(conv.id, { limit: 5, include_tool: false });
            if (!stopped) {
              mergeMessages(detail.messages || []);
              setBusy(false);
              setLiveTool(null);
            }
          } catch { /* transient — keep polling */ }
        }, 1200);
      } catch { /* progress endpoint unavailable — non-fatal */ }
    })();
    return () => { stopped = true; if (interval) clearInterval(interval); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [conv?.id]);

  // Scroll-back: load 10 older messages and prepend.
  const loadOlder = useCallback(async () => {
    if (!conv?.id || loadingMore || !hasMore) return;
    const oldestTs = messages.length > 0 ? messages[0].ts : undefined;
    if (!oldestTs) return;
    setLoadingMore(true);
    try {
      const detail = await aiAgentApi.getConversation(conv.id, {
        limit: 10, before_ts: oldestTs, include_tool: false,
      });
      const older = detail.messages || [];
      // De-dupe in case server returns boundary overlap
      const seen = new Set(messages.map((m) => m.id));
      const merged = [...older.filter((m) => !seen.has(m.id)), ...messages];
      setMessages(merged);
      setHasMore(Boolean(detail.has_more));
    } catch {
      toast.error('Could not load earlier messages');
    } finally {
      setLoadingMore(false);
    }
  }, [conv?.id, hasMore, loadingMore, messages]);

  const send = useCallback(async (content) => {
    if (!conv?.id) return;
    const userMsg = { id: `local_${Date.now()}`, role: 'user', content, ts: new Date().toISOString() };
    setMessages((prev) => [...prev, userMsg]);
    setBusy(true);
    setLiveTool(null);
    // Poll live turn-progress so the shimmer reflects the ACTUAL running tool
    // (router → each tool), not a client-side guess.
    const poll = setInterval(async () => {
      try {
        const p = await aiAgentApi.getTurnProgress(conv.id);
        setLiveTool(p?.active ? (p.tool || null) : null);
      } catch { /* non-fatal */ }
    }, 900);
    try {
      const result = await aiAgentApi.sendMessage(conv.id, content, accountId);
      const final = {
        id: `assistant_${Date.now()}`,
        role: 'assistant',
        content: result.final || '',
        report_job: result.report_job || null,
        onboarding_card: result.onboarding_card || null,
        suggested_actions: result.suggested_actions || [],
        nav_actions: result.nav_actions || [],
        chart: result.chart || null,
        intent: result.intent || null,
        trace: result.trace || result.tool_trace || [],
        ts: new Date().toISOString(),
      };
      setMessages((prev) => [...prev, final]);
      onChanged?.();
    } catch {
      toast.error('Sorry — the agent ran into a problem');
    } finally {
      clearInterval(poll);
      setLiveTool(null);
      setBusy(false);
      // Re-read the wallet so the header balance + composer gate reflect the
      // credits this turn just consumed (freezes send when we drop < 1 credit).
      refreshWallet?.();
    }
  }, [conv?.id, accountId, onChanged, refreshWallet]);

  const onAttach = async (file) => {
    if (!file) return;
    if (file.size > 25 * 1024 * 1024) {
      toast.error('File exceeds 25MB');
      return;
    }
    try {
      const pre = await aiAgentApi.presignUpload({
        filename: file.name,
        content_type: file.type || 'application/octet-stream',
        size_bytes: file.size,
        conv_id: conv?.id,
        visibility: 'private',
      });
      const putRes = await fetch(pre.upload_url, {
        method: 'PUT',
        headers: { 'Content-Type': pre.content_type },
        body: file,
      });
      if (!putRes.ok) throw new Error(`Upload failed (${putRes.status})`);
      const confirmed = await aiAgentApi.confirmUpload({
        file_id: pre.file_id,
        s3_key: pre.s3_key,
        filename: file.name,
        content_type: file.type || 'application/octet-stream',
        size_bytes: file.size,
        conv_id: conv?.id,
        visibility: 'private',
      });
      setAttachments((prev) => [...prev, { ...confirmed, file_id: confirmed.id || confirmed.file_id }]);
      toast.success(`Attached ${file.name}`);
    } catch (e) {
      toast.error(e?.message || 'Upload failed');
    }
  };

  const onSendClick = () => {
    const txt = draft.trim();
    if (!txt || busy || blocked) return;
    let content = txt;
    if (attachments.length) {
      // Include each file_id inline so the agent can call analyze_document
      // without having to first run list_drive_files.
      const lines = attachments.map((a) =>
        `  - ${a.filename} (file_id: ${a.file_id})`).join('\n');
      content += `\n\n[Attached ${attachments.length} file(s) from AI Agent Drive. Pick the right tool for the request: analyze_document to read/summarize a bill or document, OR onboard_collectors_from_file to onboard utility-portal login credentials:\n${lines}\n]`;
    }
    send(content);
    setDraft('');
    setAttachments([]);
  };

  const onKey = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      if (blocked) return; // out of credits — Enter is frozen until top-up
      onSendClick();
    }
  };

  // Auto-send a resume prompt exactly once when arriving here after a Stripe
  // top-up (or any external caller that primed a resume message). Guarded by
  // `send` being stable and `resumePrompt` non-null.
  const resumeSentRef = useRef(false);
  useEffect(() => {
    if (!resumePrompt || resumeSentRef.current || !conv?.id) return;
    resumeSentRef.current = true;
    send(resumePrompt);
    onResumeConsumed?.();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resumePrompt, conv?.id]);

  const title = conv?.title?.trim() || 'New chat';

  return (
    <div className="flex h-full flex-col">
      {/* Header */}
      <div className="flex items-center gap-2 border-b border-slate-100 px-3 py-2.5">
        <button
          data-testid="ai-chat-back"
          onClick={onBack}
          className="rounded-md p-1.5 text-slate-500 hover:bg-slate-100 hover:text-slate-900"
          aria-label="Back to chats"
        >
          <ArrowLeft className="h-4 w-4" />
        </button>
        <div className="min-w-0 flex-1 truncate text-sm font-semibold text-slate-900">{title}</div>
        <button
          data-testid="ai-invite-open"
          onClick={() => setShowInvite(true)}
          className="inline-flex items-center gap-1 rounded-lg border border-slate-200 px-2.5 py-1.5 text-xs font-semibold text-slate-700 hover:border-emerald-300 hover:text-emerald-700"
          title="Invite teammates"
        >
          <UserPlus className="h-3.5 w-3.5" />
          {collabCount > 0 ? <span className="inline-flex items-center gap-1"><Users className="h-3 w-3" />{collabCount}</span> : <span>Invite</span>}
        </button>
      </div>

      <div className="flex-1 min-h-0 flex flex-col">
        {hasMore && (
          <div className="border-b border-slate-100 bg-white px-4 py-1.5 text-center">
            <button
              data-testid="ai-chat-load-older"
              onClick={loadOlder}
              disabled={loadingMore}
              className="inline-flex items-center gap-1.5 rounded-md border border-slate-200 px-2.5 py-1 text-[11px] font-semibold text-slate-600 hover:bg-slate-50 disabled:opacity-40"
            >
              {loadingMore ? 'Loading earlier messages…' : (
                `Load earlier messages (${Math.max(0, totalMessages - messages.length)} remaining)`
              )}
            </button>
          </div>
        )}
        <MessageList messages={messages} busy={busy} statusTool={liveTool} onSuggestedAction={send} convId={conv?.id} />
      </div>

      {/* Attachment chips */}
      {attachments.length > 0 && (
        <div className="flex flex-wrap gap-1.5 border-t border-slate-100 px-3 py-2">
          {attachments.map((a) => (
            <span key={a.id} className="inline-flex items-center gap-1.5 rounded-md bg-slate-100 px-2 py-0.5 text-[11px] text-slate-700">
              <Paperclip className="h-3 w-3" />{a.filename}
              <button onClick={() => setAttachments((prev) => prev.filter((x) => x.id !== a.id))} className="ml-0.5 text-slate-400 hover:text-rose-600">×</button>
            </span>
          ))}
        </div>
      )}

      {/* Composer (paperclip + send arrow per wireframe) */}
      <div className="border-t border-slate-100 bg-white p-3" data-testid="ai-chat-composer">
        {blocked && (
          <div
            className="mb-2 flex items-center justify-between gap-2 rounded-lg border border-rose-200 bg-rose-50 px-3 py-2"
            data-testid="ai-credits-block-banner"
          >
            <div className="flex items-start gap-2 min-w-0">
              <Coins className="mt-0.5 h-4 w-4 shrink-0 text-rose-600" />
              <p className="text-[12px] leading-snug text-rose-700">
                You&apos;re out of AI credits. Top up credits to continue using {agentName}&apos;s services.
              </p>
            </div>
            <button
              type="button"
              onClick={onTopUp}
              data-testid="ai-credits-block-topup"
              className="inline-flex shrink-0 items-center gap-1 rounded-lg bg-rose-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-rose-700"
            >
              <Sparkles className="h-3.5 w-3.5" /> Top up
            </button>
          </div>
        )}
        <div className="flex items-end gap-2">
          <textarea
            ref={taRef}
            data-testid="ai-chat-input"
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={onKey}
            rows={1}
            placeholder={blocked ? `Top up credits to continue chatting with ${agentName}…` : 'Ask anything about your data or to draft a vendor email…'}
            disabled={busy}
            className="flex-1 resize-none rounded-xl border border-slate-200 bg-slate-50 px-3 py-2 text-sm outline-none transition focus:border-emerald-300 focus:bg-white focus:ring-2 focus:ring-emerald-100 overflow-y-auto"
            style={{ minHeight: 100, maxHeight: 300 }}
          />
          <label
            data-testid="ai-chat-attach"
            className={`inline-flex h-10 w-10 cursor-pointer items-center justify-center rounded-xl border border-slate-200 text-slate-500 hover:border-emerald-300 hover:text-emerald-700 ${busy || blocked ? 'opacity-40 pointer-events-none' : ''}`}
            title="Attach file"
          >
            <Paperclip className="h-4 w-4" />
            <input
              type="file"
              className="hidden"
              disabled={busy || blocked}
              onChange={(e) => { onAttach(e.target.files?.[0]); e.target.value = ''; }}
            />
          </label>
          <button
            data-testid="ai-chat-send"
            onClick={onSendClick}
            disabled={busy || blocked || (!draft.trim() && attachments.length === 0)}
            className="inline-flex h-10 w-10 items-center justify-center rounded-xl bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-40"
            aria-label="Send"
          >
            <Send className="h-4 w-4" />
          </button>
        </div>
      </div>

      {showInvite && conv?.id && (
        <InvitePicker
          convId={conv.id}
          onClose={() => setShowInvite(false)}
          onChanged={() => onChanged?.()}
        />
      )}
    </div>
  );
};
